import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { ArrowLeft, DownloadSimple, Printer, ShieldCheck, FileText } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvoicePage() {
  const { bookingId } = useParams();
  const { token } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/invoices/${bookingId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setInvoice(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [bookingId, token]);

  const handlePrint = () => window.print();

  if (loading) return (
    <div className="min-h-screen bg-background"><Navbar />
      <div className="flex items-center justify-center py-24"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
    </div>
  );

  if (!invoice) return (
    <div className="min-h-screen bg-background"><Navbar />
      <div className="max-w-lg mx-auto px-6 py-24 text-center">
        <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
          <FileText size={26} className="text-primary" />
        </div>
        <p className="text-muted-foreground">Invoice not found.</p>
        <Link to="/dashboard" className="inline-flex items-center justify-center text-sm font-medium text-primary hover:underline mt-3 min-h-[44px]">Back to Dashboard</Link>
      </div>
    </div>
  );

  const statusColor = { pending: 'text-amber-700 bg-amber-50', confirmed: 'text-primary bg-secondary', completed: 'text-emerald-700 bg-emerald-50', cancelled: 'text-destructive bg-destructive/10' };

  return (
    <div className="min-h-screen bg-background" data-testid="invoice-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Actions bar */}
        <div className="flex items-center justify-between mb-6 print:hidden">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors min-h-[44px]" data-testid="back-link">
            <ArrowLeft size={16} /> Back
          </Link>
          <div className="flex gap-2">
            <button onClick={handlePrint} className="inline-flex items-center gap-1.5 border border-border bg-card rounded-xl px-4 h-11 text-sm font-medium text-foreground hover:bg-secondary/60 btn-anim cursor-pointer" data-testid="print-invoice">
              <Printer size={15} /> Print
            </button>
          </div>
        </div>

        {/* Invoice card */}
        <div className="bg-card rounded-2xl border border-border/70 shadow-soft overflow-hidden fade-up print:border-0 print:shadow-none" data-testid="invoice-card">
          {/* Header */}
          <div className="px-8 py-7 border-b border-border flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-display font-semibold tracking-tight text-foreground">Bookr.ai</h1>
              <p className="text-xs text-muted-foreground mt-1">Marketplace for local services</p>
            </div>
            <div className="text-right">
              <p className="text-xl font-display font-semibold tracking-tight text-foreground">Invoice</p>
              <p className="text-xs text-muted-foreground font-mono mt-1">{invoice.invoice_number}</p>
            </div>
          </div>

          {/* Meta */}
          <div className="px-8 py-6 border-b border-border grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-widest text-primary mb-2">Bill to</p>
              <p className="font-medium text-foreground">{invoice.customer.name}</p>
              <p className="text-muted-foreground text-xs mt-0.5">{invoice.customer.email}</p>
              {invoice.customer.city && <p className="text-muted-foreground text-xs">{invoice.customer.city}</p>}
            </div>
            <div>
              <p className="text-[11px] font-medium uppercase tracking-widest text-primary mb-2">Provider</p>
              <p className="font-medium text-foreground">{invoice.provider.name}</p>
              <p className="text-muted-foreground text-xs mt-0.5">{invoice.provider.category}</p>
              {invoice.provider.city && <p className="text-muted-foreground text-xs">{invoice.provider.city}</p>}
            </div>
            <div>
              <p className="text-[11px] font-medium uppercase tracking-widest text-primary mb-2">Details</p>
              <p className="text-foreground text-xs">Date: {invoice.booking_date}</p>
              <p className="text-muted-foreground text-xs mt-0.5">Time: {invoice.start_time || '—'}</p>
              <p className="text-muted-foreground text-xs">Issued: {invoice.date ? new Date(invoice.date).toLocaleDateString() : '—'}</p>
              <div className="mt-2">
                <span className={`rounded-full text-xs font-medium px-3 py-1 capitalize ${statusColor[invoice.status] || 'text-muted-foreground bg-secondary'}`}>
                  {invoice.status}
                </span>
              </div>
            </div>
          </div>

          {/* Line items */}
          <div className="px-8 py-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2.5 text-[11px] font-medium uppercase tracking-widest text-muted-foreground">Service</th>
                  <th className="text-right py-2.5 text-[11px] font-medium uppercase tracking-widest text-muted-foreground">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border/60">
                  <td className="py-4">
                    <p className="font-medium text-foreground">{invoice.service.title}</p>
                    {invoice.service.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{invoice.service.description}</p>}
                    {invoice.location && <p className="text-xs text-muted-foreground mt-0.5">{invoice.location}</p>}
                  </td>
                  <td className="py-4 text-right font-medium text-foreground tabular-nums">${invoice.subtotal.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="px-8 py-6 border-t border-border bg-secondary/40">
            <div className="max-w-xs ml-auto space-y-2.5 text-sm">
              <div className="flex justify-between items-baseline pb-2">
                <span className="font-medium text-foreground">Total paid ({invoice.currency})</span>
                <span className="text-2xl font-display font-semibold text-foreground tabular-nums">${invoice.total.toFixed(2)}</span>
              </div>
              {/* The listed price is split between Bookr's fee and the provider —
                  the customer pays no add-on (fee + payout = total). */}
              <div className="flex justify-between text-xs text-muted-foreground pt-2 border-t border-border">
                <span>Bookr service fee ({invoice.platform_fee_percent}%)</span>
                <span className="tabular-nums">−${invoice.platform_fee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Provider receives</span>
                <span className="tabular-nums">${invoice.provider_payout.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-8 py-4 border-t border-border flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck size={15} className="text-primary" /> Protected by Bookr.ai &middot; Escrow payment
            </div>
            {invoice.stripe_session_id && (
              <p className="text-[10px] text-muted-foreground font-mono">Ref: {invoice.stripe_session_id.slice(0, 20)}...</p>
            )}
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="px-8 py-4 border-t border-border/60">
              <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground mb-1.5">Notes</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{invoice.notes}</p>
            </div>
          )}
        </div>

        {/* Tax notice */}
        <div className="mt-5 px-4 py-3 text-xs text-muted-foreground text-center leading-relaxed print:hidden">
          This invoice is generated for record-keeping purposes. GST/QST may apply based on your province. Bookr.ai is a marketplace platform — services are provided by independent contractors.
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          nav, [data-testid="back-link"], [data-testid="print-invoice"], .print\\:hidden { display: none !important; }
          body { background: white !important; }
          [data-testid="invoice-card"] { box-shadow: none !important; border: none !important; }
        }
      `}</style>
    </div>
  );
}
